import { useCallback, useEffect, useState } from 'react';
import { getUserDetails, makeGetCall, makePostCall } from '../../../utils/helper';
import type { OoReport, OrderDetails, PatientNoteGroup, RadUser } from '../types';

/**
 * OnlyOffice variant of useReportData.
 *
 * Calls /get-reports-oodocs and /get-prior-reports-oodocs which return
 * reports with a nested `document` object (doc_id, doc_key, etc.).
 * Everything else (notes, users) uses the same endpoints as before.
 */
export const useReportDataOodocs = (orderDetails: OrderDetails | null) => {
  const [reports, setReports] = useState<OoReport[]>([]);
  const [currentReport, setCurrentReport] = useState<OoReport | null>(null);
  const [priorReports, setPriorReports] = useState<OoReport[]>([]);
  const [notes, setNotes] = useState<PatientNoteGroup[]>([]);
  const [radUsers, setRadUsers] = useState<RadUser[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [isSignedOff, setIsSignedOff] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const pacs_order = orderDetails?.pacs_order;
  const patient = pacs_order?.patient;

  // ── Reports for this order (with document info) ──
  // opts.selectDocId: after a fork (Draft/Reviewed copy) the caller passes the new doc ID
  // so the freshly created row becomes the working surface (and autosave target).
  const fetchReports = useCallback(async (opts: { selectDocId?: number } = {}) => {
    if (!pacs_order) return;
    try {
      const res = await makePostCall('/get-reports-oodocs', {
        order_id: pacs_order.pacs_ord_id,
        user_id: getUserDetails()?.username,
      });
      const data: OoReport[] = res.data?.data || [];
      setReports(data);

      const hasSignedOff = data.some(r => r.pr_status === 'SIGNEDOFF');
      setIsSignedOff(hasSignedOff);

      if (data.length > 0) {
        setCurrentReport(prev => {
          // Signed-off order: the newest signed snapshot IS the surface. It opens in edit
          // mode but nothing persists server-side — re-reporting goes through sign-off only.
          // (Legacy TinyMCE-era signed rows have no linked document — skip those.)
          const signed = data.find(r => r.pr_status === 'SIGNEDOFF' && r.document?.doc_id);
          if (signed) return signed;
          // Explicit switch to a just-forked copy
          if (opts.selectDocId) {
            const target = data.find(r => r.document?.doc_id === opts.selectDocId);
            if (target) return target;
          }
          // Keep the current selection if its row still exists (status may have changed)
          const stillThere = prev?.pr_id ? data.find(r => r.pr_id === prev.pr_id) : null;
          if (stillThere) return stillThere;
          // Newest editable working copy (reviewed > draft > autosaved by recency),
          // else fall back to the newest row (handled defensively by the editor).
          const editable = (r: OoReport) => r.document?.doc_id && r.document.doc_status !== 'finalized';
          return data.find(editable) || data[0];
        });
      } else {
        setCurrentReport(null);
      }
      setInitialFetchDone(true);
    } catch (e) {
      console.error('Failed to fetch oodocs reports:', e);
      setInitialFetchDone(true);
    }
  }, [pacs_order?.pacs_ord_id]);

  // ── Prior reports (other orders for same patient, with document info) ──
  const fetchPriorReports = useCallback(async () => {
    if (!pacs_order || !patient) return;
    try {
      const res = await makePostCall('/get-prior-reports-oodocs', {
        order_id: pacs_order.pacs_ord_id,
        yh_no: patient.pat_pin,
      });
      setPriorReports(res.data?.data || []);
    } catch {
      setPriorReports([]);
    }
  }, [pacs_order?.pacs_ord_id, patient?.pat_pin]);

  // ── Patient notes / documents (same endpoint as before) ──
  const fetchNotes = useCallback(async () => {
    if (!pacs_order || !patient) return;
    try {
      const res = await makePostCall('/pat-prev-notes', {
        user_id: getUserDetails()?.username,
        pin: patient.pat_pin,
        order_id: pacs_order.po_his_ord_no,
        ord_no: pacs_order.pacs_ord_id,
      });
      setNotes(res.data?.data || []);
    } catch {
      setNotes([]);
    }
  }, [pacs_order?.pacs_ord_id, patient?.pat_pin]);

  // ── Rad users (for co-sign dropdown) ──
  const fetchRadUsers = useCallback(async () => {
    try {
      const res = await makeGetCall('/get-rad-users');
      setRadUsers(res.data?.data || []);
    } catch {
      setRadUsers([]);
    }
  }, []);

  // ── All users (for proxy draft dropdown) ──
  const fetchAllUsers = useCallback(async () => {
    try {
      const res = await makePostCall('/user-list', {});
      setAllUsers(res.data?.data || []);
    } catch {
      setAllUsers([]);
    }
  }, []);

  // Fetch everything when order changes
  useEffect(() => {
    if (!orderDetails) return;
    fetchReports();
    fetchPriorReports();
    fetchNotes();
  }, [orderDetails]);

  // Fetch user lists once on mount
  useEffect(() => {
    fetchRadUsers();
    fetchAllUsers();
  }, []);

  return {
    reports,
    currentReport,
    setCurrentReport,
    priorReports,
    notes,
    radUsers,
    allUsers,
    isSignedOff,
    setIsSignedOff,
    initialFetchDone,
    refetchReports: fetchReports,
  };
};
